import { useEffect, useRef, useState } from 'react';
import * as Ably from 'ably';
import { getRealtimeTokenApi } from '../apis';
import { realtimeChannels } from '@/utils/functions';
import { useLogger } from './useLogger';

const RELOAD_SCREEN = 2;

/**
 * Connects the screen to Ably. The token comes from our API (authenticated with the screen token)
 * and the server decides what this screen may do: listen to its tenant and own screen channels,
 * be present, and stream its live view. Publishing commands is server-only.
 *
 * - "schedule-updated" on the tenant channel → `onScheduleUpdated` (in-place re-fetch)
 * - "force-reload" on this screen's channel → full page reload
 * - "identify" on this screen's channel → `onIdentify` (video wall tile shows its position)
 * - while a console monitor ("live_*") is present, `liveSnapshot` is streamed on every change
 */
export const useRealtime = ({
  enabled,
  tid,
  screenId,
  onScheduleUpdated,
  onIdentify,
  liveSnapshot,
}) => {
  const [liveView, setLiveView] = useState(false);
  const [liveRequested, setLiveRequested] = useState(0);
  const liveChannelRef = useRef(null);
  const onScheduleUpdatedRef = useRef(onScheduleUpdated);
  onScheduleUpdatedRef.current = onScheduleUpdated;
  const onIdentifyRef = useRef(onIdentify);
  onIdentifyRef.current = onIdentify;
  const { LogError, LogToExternal } = useLogger();

  useEffect(() => {
    if (!enabled || !tid || !screenId) return;

    let ably;
    let channels;
    try {
      ably = new Ably.Realtime({
        authCallback: (_tokenParams, callback) => {
          getRealtimeTokenApi()
            .then((response) => callback(null, response.data))
            .catch((error) =>
              callback(error?.message || 'Realtime auth failed', null),
            );
        },
      });

      channels = {
        tenant: ably.channels.get(realtimeChannels.tenant(tid)),
        screen: ably.channels.get(realtimeChannels.screen(tid, screenId)),
        presence: ably.channels.get(realtimeChannels.presence(tid)),
        live: ably.channels.get(realtimeChannels.live(tid, screenId)),
      };
      liveChannelRef.current = channels.live;

      // Announce this screen (online status + live monitor).
      channels.presence.presence.enter().catch(() => {});
      channels.live.presence.enter().catch(() => {});

      // Go live whenever a console monitor (clientId "live_<userId>") is on this screen's channel.
      const syncLiveView = () =>
        channels.live.presence
          .get()
          .then((members) =>
            setLiveView(
              (members || []).some((m) => m.clientId?.startsWith('live')),
            ),
          )
          .catch(() => {});
      channels.live.presence.subscribe(
        ['enter', 'leave', 'present', 'update'],
        syncLiveView,
      );
      syncLiveView();

      // The monitor asks for an immediate frame when it opens.
      channels.live.subscribe('client-live-mode', (message) => {
        if (message.data?.status) setLiveRequested((n) => n + 1);
      });

      channels.tenant.subscribe('schedule-updated', () =>
        onScheduleUpdatedRef.current?.(),
      );

      channels.screen.subscribe('force-reload', ({ data }) => {
        if (data?.reloadType === RELOAD_SCREEN && data.screenId === screenId) {
          LogError(0, `Force Reload ${JSON.stringify(data)}`);
          window.location.reload();
        }
      });

      channels.screen.subscribe('identify', ({ data }) => {
        if (data?.screenId === screenId) onIdentifyRef.current?.(data);
      });
    } catch (error) {
      LogToExternal('error', error);
      LogError(1, `Exception:startRealtime ${error.message}`);
    }

    return () => {
      liveChannelRef.current = null;
      if (!ably || !channels) return;
      channels.presence.presence.leave().catch(() => {});
      channels.live.presence.leave().catch(() => {});
      Object.values(channels).forEach((channel) => channel.unsubscribe());
      ably.close();
    };
  }, [enabled, tid, screenId]);

  // Stream what is on screen while being watched.
  useEffect(() => {
    if (!liveView || !liveSnapshot || !liveChannelRef.current) return;
    liveChannelRef.current
      .publish('client-live-screen', liveSnapshot)
      .catch(() => {});
  }, [liveView, liveSnapshot, liveRequested]);
};
